import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SearchLg, X } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { Input } from "@/components/base/input/input";
import { FilterTag } from "../filter-tag/filter-tag";
import { listTags } from "@fp/admin-api-client/client";
import { HttpStatusCode } from "axios";
import { Modal, ModalOverlay, Dialog } from "../modals/modal";
import { Heading } from "react-aria-components";
import { AddButton } from "@/components/base/buttons/add-button";
import { Button } from "@/components/base/buttons/button";
import {
  TagForm,
  TagFormContent,
  TagFormActions,
  TagFormErrorMessage,
} from "../../tags/tags-form";

export const TagSelector = ({
  selectedTags,
  onChange,
}: {
  selectedTags: string[];
  onChange: (tagIds: string[]) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: tags, isLoading: areTagsLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const response = await listTags(undefined);
      if (response.status !== HttpStatusCode.Ok) {
        throw new Error("Network response was not ok");
      }
      return response.data.items ?? [];
    },
  });

  const filteredTags = useMemo(() => {
    if (!tags) return [];
    if (!searchTerm.trim()) return tags;

    return tags.filter(
      (tag) =>
        tag.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ??
        false,
    );
  }, [tags, searchTerm]);

  const handleTagCreated = async () => {
    await queryClient.invalidateQueries({ queryKey: ["tags"] });
    setIsModalOpen(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <Input
          className="flex-1"
          label=""
          name="tagName"
          icon={SearchLg}
          value={searchTerm}
          onChange={setSearchTerm}
        />
        <AddButton onClick={() => setIsModalOpen(true)}>New Tag</AddButton>
      </div>
      {areTagsLoading && <div>Loading tags...</div>}
      {!areTagsLoading && (
        <div className="mt-8 flex flex-wrap gap-2">
          {filteredTags.map((tag) => (
            <FilterTag
              key={tag.id}
              tag={tag}
              selected={selectedTags.some((tid) => tid === tag.id)}
              onClick={(tag) => {
                if (!tag.id) return;
                if (selectedTags.some((tid) => tid === tag.id)) {
                  onChange(
                    selectedTags
                      .filter((tid) => tid !== tag.id)
                      .map((tid) => tid),
                  );
                } else {
                  onChange([...selectedTags, tag.id]);
                }
              }}
            />
          ))}
        </div>
      )}

      <ModalOverlay isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal className="max-w-md">
          <Dialog>
            <div className="bg-white rounded-lg p-6 shadow-xl flex-grow">
              <div className="flex justify-between items-center mb-4">
                <Heading slot="title" level={2} className="text-lg font-semibold">
                  Create New Tag
                </Heading>
                <Button
                  color="tertiary"
                  size="sm"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <TagForm tag={null} onSuccess={handleTagCreated}>
                <TagFormErrorMessage />
                <TagFormContent />
                <TagFormActions />
              </TagForm>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
};
